/**
 * DimensionDetailPanel — the provenance click-through of the 7×6 matrix
 * (DC-1613 2d, matrix spec §4).
 *
 * Renders one dimension's placement in full: the floor/reach/confidence
 * sentence, the criteria table with per-criterion citations back to the
 * vendored framework text (the moat made visible), the d1 domain-evidence
 * block, and the self-claim line. Pending dimensions expand to their reason
 * + what unlocks them — the click never dead-ends.
 *
 * Honesty postures carried in copy, not just code: the compliance report is
 * labeled supplemental (it informs, never feeds thresholds — the tenancy gap
 * documented in the aggregator header, surfaced not buried), and the
 * self-claim framing augments the workshop, never overrules it (§6.4).
 */

import React from 'react';

import type {
  D1Evidence,
  DimensionEvidence,
  DimensionPlacement,
} from './maturityContract';

const D1_MATERIALITY_DATASETS = 3;

function levelWord(level: number | null): string {
  return level === null ? '—' : `L${level}`;
}

function placementSentence(p: DimensionPlacement): string {
  if (!p.assessable || p.floor === null) {
    return 'Not yet assessable — no level is asserted for this dimension.';
  }
  const parts = [`Evidence places the floor at L${p.floor}`];
  if (p.reach !== null && p.reach > p.floor) {
    const n = p.blocking.length;
    parts.push(
      `L${p.reach} behaviours are present, blocked by ${n} unmet ${n === 1 ? 'criterion' : 'criteria'}`,
    );
  } else {
    parts.push('no higher-level behaviours are in reach yet');
  }
  parts.push(`confidence ${p.confidence}`);
  return `${parts.join(' · ')}.`;
}

function CriteriaTable({ placement }: { placement: DimensionPlacement }): React.ReactElement {
  const reach = placement.reach;
  return (
    <table className="omm-detail-table">
      <caption className="omm-visually-hidden">
        Rubric criteria evaluated for this dimension, with thresholds, observations, and framework
        citations
      </caption>
      <thead>
        <tr>
          <th scope="col">Level</th>
          <th scope="col">Criterion</th>
          <th scope="col" className="omm-num">Threshold</th>
          <th scope="col" className="omm-num">Observed</th>
          <th scope="col">Status</th>
          <th scope="col">Citation</th>
        </tr>
      </thead>
      <tbody>
        {placement.criteria.map((c) => {
          const blocking = !c.satisfied && reach !== null && c.level <= reach;
          return (
            <tr key={c.id}>
              <td className="omm-num">L{c.level}</td>
              <td>{c.description}</td>
              <td className="omm-num">{c.threshold}</td>
              <td className="omm-num">
                {c.observed === null ? (
                  <span title="no input available">—</span>
                ) : (
                  c.observed
                )}
              </td>
              <td>
                <span className={blocking ? 'omm-crit-blocking' : undefined}>
                  {c.satisfied ? 'met' : 'not met'}
                </span>
              </td>
              <td className="omm-citation">
                {c.citation.source_file} · {c.citation.level_label} · {c.citation.catalog_version}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function D1EvidenceBlock({ evidence }: { evidence: D1Evidence }): React.ReactElement {
  return (
    <div className="omm-evidence">
      <h4 className="omm-detail-subhead">Evidence — domain rollup</h4>
      <p className="omm-evidence-read">
        Weighted mean{' '}
        <strong>{evidence.weighted_mean === null ? '—' : evidence.weighted_mean}</strong> across{' '}
        {evidence.evaluated_datasets} of {evidence.total_datasets} datasets.{' '}
        {evidence.min_material_domain
          ? `Consistency reads from your weakest material domain — ${evidence.min_material_domain.domain_name}, mean ${evidence.min_material_domain.mean}.`
          : 'No domain reaches materiality yet, so consistency is unverifiable — the L3+ consistency criteria read unsatisfied, never assumed.'}
      </p>
      <table className="omm-detail-table">
        <caption className="omm-visually-hidden">
          Contributing domains with evaluated dataset counts and mean FAIR scores
        </caption>
        <thead>
          <tr>
            <th scope="col">Domain</th>
            <th scope="col" className="omm-num">Evaluated / total</th>
            <th scope="col" className="omm-num">Mean</th>
            <th scope="col">Materiality</th>
          </tr>
        </thead>
        <tbody>
          {evidence.domains.map((d) => (
            <tr key={d.domain_id}>
              <td>{d.domain_name}</td>
              <td className="omm-num">
                {d.evaluated_datasets} / {d.total_datasets}
              </td>
              <td className="omm-num">{d.mean}</td>
              <td>
                {d.material ? (
                  'material'
                ) : (
                  <span className="omm-low-n-tag">
                    below materiality (n &lt; {D1_MATERIALITY_DATASETS})
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {evidence.compliance && (
        <p className="omm-supplemental">
          Policy-vs-practice report (supplemental — informs, never feeds thresholds):{' '}
          {evidence.compliance.documents_analyzed} governance document(s) vs{' '}
          {evidence.compliance.datasets_analyzed} dataset(s), overall compliance{' '}
          {evidence.compliance.overall_compliance_score}%.
        </p>
      )}
    </div>
  );
}

export interface DimensionDetailPanelProps {
  placement: DimensionPlacement;
  evidence: DimensionEvidence | undefined;
  /** Self-claimed level recorded this session, when present. */
  selfClaim: number | null;
}

export const DimensionDetailPanel: React.FC<DimensionDetailPanelProps> = ({
  placement,
  evidence,
  selfClaim,
}) => {
  if (!placement.assessable) {
    const reason =
      evidence && evidence.kind === 'pending' ? evidence.reason : placement.evidence_summary;
    return (
      <div className="omm-detail" data-testid={`detail-${placement.dimension}`}>
        <p className="omm-detail-pending">{reason}</p>
        <p className="omm-detail-unlock">
          This dimension places itself when its evidence path ships — narrative-dimension inference
          (2c) is calibration-gated and arrives only after it clears the agreement bar on the
          expert-labelled reference set.
        </p>
      </div>
    );
  }

  return (
    <div className="omm-detail" data-testid={`detail-${placement.dimension}`}>
      <p className="omm-detail-read">{placementSentence(placement)}</p>
      <p className="omm-detail-method">
        method: deterministic rubric · {placement.rubric_version ?? '—'}
      </p>

      <h4 className="omm-detail-subhead">Criteria</h4>
      <CriteriaTable placement={placement} />

      {evidence && evidence.kind === 'd1_technical' && <D1EvidenceBlock evidence={evidence} />}

      {selfClaim !== null && (
        <p className="omm-selfclaim-line">
          Self-assessed {levelWord(selfClaim)} in the organization&rsquo;s own workshop. Compass
          surfaces where perception and evidence diverge — it does not overrule the workshop.
        </p>
      )}
    </div>
  );
};

export default DimensionDetailPanel;
